import { useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, FileCheck, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const KIND_CONFIG = {
  pdf: { accept: "application/pdf", endpoint: "pdf", label: "PDF", verify: (f) => f.type === "application/pdf", verifyMsg: "Please choose a PDF file." },
  image: { accept: "image/*", endpoint: "image", label: "image", verify: (f) => f.type.startsWith("image/"), verifyMsg: "Please choose an image." },
  document: {
    accept: ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    endpoint: "document",
    label: "PDF or Word doc",
    verify: (f) =>
      f.type === "application/pdf" ||
      f.type === "application/msword" ||
      f.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      /\.(pdf|doc|docx)$/i.test(f.name),
    verifyMsg: "Please choose a PDF or Word document (.pdf, .doc, .docx).",
  },
};

/**
 * Single-file uploader for PDFs, images, or PDF+Word documents.
 * Supports drag-and-drop and click-to-browse.
 *
 * Props:
 * - kind: "pdf" | "image" | "document"
 * - value: current file id (string) or ""
 * - onChange: (fileId: string) => void
 * - testKey: data-testid prefix
 */
const FileUploader = ({ kind = "pdf", value = "", onChange, testKey = "file" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const cfg = KIND_CONFIG[kind] || KIND_CONFIG.pdf;

  const upload = async (file) => {
    if (!file) return;
    if (!cfg.verify(file)) {
      toast.error(cfg.verifyMsg);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/files/upload/${cfg.endpoint}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(res.data.id);
      toast.success(`Uploaded ${file.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onPick = (e) => upload(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    upload(e.dataTransfer.files?.[0]);
  };

  const clear = () => onChange?.("");
  const downloadUrl = value ? `${API_BASE}/files/${value}` : null;

  return (
    <div className="flex flex-col gap-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed transition-colors p-5 text-center ${
          over ? "border-accent bg-accent/10" : "border-border bg-muted/40 hover:bg-muted/60"
        }`}
        data-testid={`${testKey}-dropzone`}
      >
        {busy ? (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 size={16} className="animate-spin" /> Uploading...
          </div>
        ) : value ? (
          <div className="flex items-center justify-center gap-2 text-sm">
            <FileCheck size={16} className="text-accent" />
            <span className="font-medium">File uploaded</span>
            <span className="text-muted-foreground">— click or drop to replace</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-sm">
            <Upload size={18} className="text-accent" />
            <div className="font-medium">Drag & drop {cfg.label} here</div>
            <div className="text-xs text-muted-foreground">or click to browse</div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={cfg.accept}
          className="hidden"
          onChange={onPick}
          data-testid={`${testKey}-input`}
        />
      </div>

      {value && (
        <div className="flex items-center justify-between gap-2 text-sm">
          <a
            href={downloadUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-secondary hover:text-primary"
            data-testid={`${testKey}-preview-link`}
          >
            <FileCheck size={14} /> View uploaded
          </a>
          <Button type="button" variant="ghost" size="sm" onClick={clear} data-testid={`${testKey}-clear-btn`}>
            <X size={14} className="mr-1" /> Remove
          </Button>
        </div>
      )}

      {value && kind === "image" && (
        <img
          src={downloadUrl}
          alt="Preview"
          className="max-h-40 rounded-md border border-border object-cover"
        />
      )}

      {/* hidden fallback button — kept for backwards compatibility with tests */}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="hidden"
        data-testid={`${testKey}-pick-btn`}
      />
    </div>
  );
};

export default FileUploader;
